import { useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { Plus, Trash, PencilSimple, EnvelopeSimple, Phone, Buildings, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Field, Modal } from "./AVGS";

const BILLING_OPTIONS = [
  { value: "", label: "— Nicht festgelegt —" },
  { value: "fax", label: "Per Fax" },
  { value: "post", label: "Per Post" },
  { value: "portal", label: "Über Portal" },
  { value: "email", label: "Per E-Mail" },
];

const empty = {
  name: "",
  type: "AVGS",
  customer_number: "",
  jobcenter_name: "",
  jobcenter_fax: "",
  jobcenter_email: "",
  billing_method: "",
  billing_portal_url: "",
  email: "",
  phone: "",
  notes: "",
};

function billingLabel(v) {
  return BILLING_OPTIONS.find((o) => o.value === v)?.label || "—";
}

function billingClass(v) {
  return {
    fax: "bg-[#FFF1EE] text-[#FF6B5C]",
    post: "bg-[#E6F7F7] text-[#0AA09E]",
    portal: "bg-[#F1F5F9] text-[#1F242E]",
    email: "bg-[#E6F7F7] text-[#0AA09E]",
  }[v] || "bg-[#F1F5F9] text-[#94A3B8]";
}

export default function CustomersPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterBilling, setFilterBilling] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await api.get("/customers");
    setItems(data);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (it) => {
    setEditing(it.id);
    setForm({
      name: it.name || "",
      type: it.type || "AVGS",
      customer_number: it.customer_number || "",
      jobcenter_name: it.jobcenter_name || "",
      jobcenter_fax: it.jobcenter_fax || "",
      jobcenter_email: it.jobcenter_email || "",
      billing_method: it.billing_method || "",
      billing_portal_url: it.billing_portal_url || "",
      email: it.email || "",
      phone: it.phone || "",
      notes: it.notes || "",
    });
    setOpen(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/customers/${editing}`, form);
        toast.success("Teilnehmer aktualisiert");
      } else {
        await api.post("/customers", form);
        toast.success("Teilnehmer angelegt");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Wirklich löschen? (Erscheint beim nächsten Aufruf erneut wenn der Name in Rechnungen verwendet wird.)")) return;
    await api.delete(`/customers/${id}`);
    toast.success("Gelöscht");
    load();
  };

  const filtered = items.filter((it) => {
    if (search) {
      const q = search.toLowerCase().trim();
      const hay = `${it.name} ${it.customer_number} ${it.jobcenter_name} ${it.email}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (filterType !== "all" && it.type !== filterType) return false;
    if (filterBilling !== "all" && (it.billing_method || "") !== filterBilling) return false;
    return true;
  });

  const incomplete = items.filter((it) => !it.jobcenter_name || !it.customer_number).length;

  return (
    <div className="space-y-6" data-testid="customers-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-eyebrow">Teilnehmer</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold mt-1.5">Teilnehmer-Verzeichnis</h1>
          <p className="text-sm text-[#5B6371] mt-1">
            Alle Teilnehmer (automatisch aus AVGS- & FBW-Rechnungen). Hier hinterlegst du Kundennummer, Jobcenter & Abrechnungsweg.
          </p>
        </div>
        <button onClick={openCreate}
          data-testid="customer-create-button"
          className="inline-flex items-center gap-2 bg-[#0AA09E] hover:bg-[#088987] text-white text-sm font-medium rounded-md px-4 py-2.5">
          <Plus size={16} weight="bold" /> Teilnehmer hinzufügen
        </button>
      </header>

      {/* Filter */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-3 md:p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[180px]">
            <div className="label-eyebrow mb-1.5">Suche</div>
            <div className="relative">
              <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Name, Kunden-Nr., Jobcenter …"
                className="field !pl-8" data-testid="customer-search" />
            </div>
          </div>
          <div className="min-w-[140px]">
            <div className="label-eyebrow mb-1.5">Typ</div>
            <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="field" data-testid="customer-filter-type">
              <option value="all">Alle</option>
              <option value="AVGS">AVGS</option>
              <option value="FBW">FBW</option>
            </select>
          </div>
          <div className="min-w-[160px]">
            <div className="label-eyebrow mb-1.5">Abrechnungsweg</div>
            <select value={filterBilling} onChange={(e) => setFilterBilling(e.target.value)} className="field" data-testid="customer-filter-billing">
              <option value="all">Alle</option>
              <option value="">Nicht festgelegt</option>
              <option value="fax">Fax</option>
              <option value="post">Post</option>
              <option value="portal">Portal</option>
              <option value="email">E-Mail</option>
            </select>
          </div>
        </div>
        {incomplete > 0 && (
          <div className="mt-3 text-xs bg-[#FFF1EE] border border-[#FFD9D2] text-[#FF6B5C] rounded-md px-3 py-2">
            ⓘ {incomplete} Teilnehmer ohne Kundennummer oder Jobcenter — bitte vervollständigen.
          </div>
        )}
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="customers-table">
            <thead>
              <tr className="text-xs uppercase tracking-widest text-[#5B6371] border-b-2 border-[#E5E7EB] bg-[#F8FAFC]">
                <th className="text-left px-4 py-3 font-medium">Name</th>
                <th className="text-left px-4 py-3 font-medium">Kunden-Nr.</th>
                <th className="text-left px-4 py-3 font-medium">Jobcenter</th>
                <th className="text-left px-4 py-3 font-medium">Fax</th>
                <th className="text-left px-4 py-3 font-medium">Abrechnung</th>
                <th className="text-left px-4 py-3 font-medium">Typ</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (<tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-[#94A3B8]">Lade …</td></tr>)}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-[#94A3B8]">
                  {items.length === 0 ? "Noch keine Teilnehmer." : "Keine Treffer."}
                </td></tr>
              )}
              {filtered.map((it) => {
                const isIncomplete = !it.jobcenter_name || !it.customer_number;
                return (
                  <tr key={it.id} className={`border-b border-[#F1F5F9] last:border-0 hover:bg-[#F8FAFC] ${isIncomplete ? "bg-[#FFFCF8]" : ""}`} data-testid={`customer-row-${it.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium">{it.name}</div>
                      {it.email && (
                        <div className="text-[10px] text-[#94A3B8] mt-0.5 flex items-center gap-1">
                          <EnvelopeSimple size={10} /> {it.email}
                        </div>
                      )}
                      {it.phone && (
                        <div className="text-[10px] text-[#94A3B8] flex items-center gap-1">
                          <Phone size={10} /> {it.phone}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{it.customer_number || <span className="text-[#94A3B8]">—</span>}</td>
                    <td className="px-4 py-3 text-[#5B6371]">
                      {it.jobcenter_name ? (
                        <div className="flex items-center gap-1.5">
                          <Buildings size={12} className="text-[#94A3B8]" />
                          {it.jobcenter_name}
                        </div>
                      ) : (
                        <span className="text-[#94A3B8] text-xs">— nicht angegeben</span>
                      )}
                      {it.jobcenter_email && (
                        <div className="text-[10px] text-[#94A3B8] mt-0.5">{it.jobcenter_email}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs">{it.jobcenter_fax || <span className="text-[#94A3B8]">—</span>}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block text-[10px] uppercase tracking-wider font-medium px-2 py-0.5 rounded ${billingClass(it.billing_method)}`}>
                        {billingLabel(it.billing_method)}
                      </span>
                      {it.billing_method === "portal" && it.billing_portal_url && (
                        <div className="text-[10px] text-[#94A3B8] mt-0.5 truncate max-w-[200px]">{it.billing_portal_url}</div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-[10px] px-2 py-0.5 rounded font-medium ${
                        it.type === "AVGS" ? "bg-[#E6F7F7] text-[#0AA09E]" : "bg-[#FFF1EE] text-[#FF6B5C]"
                      }`}>{it.type}</span>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => openEdit(it)} className="text-[#5B6371] hover:text-[#0AA09E] p-1.5" data-testid={`customer-edit-${it.id}`}>
                        <PencilSimple size={16} />
                      </button>
                      <button onClick={() => onDelete(it.id)} className="text-[#5B6371] hover:text-[#FF6B5C] p-1.5" data-testid={`customer-delete-${it.id}`}>
                        <Trash size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <Modal title={editing ? "Teilnehmer bearbeiten" : "Neuer Teilnehmer"} onClose={() => setOpen(false)}>
          <form onSubmit={onSubmit} className="space-y-4" data-testid="customer-form">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Name">
                <input required className="field" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="customer-name" />
              </Field>
              <Field label="Typ">
                <select className="field" value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value })}
                  data-testid="customer-type">
                  <option value="AVGS">AVGS</option>
                  <option value="FBW">FBW</option>
                </select>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Kunden-Nr.">
                <input className="field font-mono" value={form.customer_number}
                  onChange={(e) => setForm({ ...form, customer_number: e.target.value })}
                  data-testid="customer-number" placeholder="z.B. 123456789" />
              </Field>
              <Field label="Telefon">
                <input className="field" value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  data-testid="customer-phone" />
              </Field>
            </div>
            <Field label="E-Mail des Teilnehmers">
              <input type="email" className="field" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="customer-email" />
            </Field>

            {/* Jobcenter Block */}
            <div className="border-t border-[#E5E7EB] pt-4">
              <div className="label-eyebrow mb-3 flex items-center gap-1.5">
                <Buildings size={14} /> Jobcenter / Bundesagentur
              </div>
              <div className="space-y-3">
                <Field label="Name des Jobcenters">
                  <input className="field" value={form.jobcenter_name}
                    onChange={(e) => setForm({ ...form, jobcenter_name: e.target.value })}
                    data-testid="customer-jobcenter-name"
                    placeholder="z.B. Jobcenter Berlin Mitte" />
                </Field>
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Fax-Nr.">
                    <input className="field font-mono" value={form.jobcenter_fax}
                      onChange={(e) => setForm({ ...form, jobcenter_fax: e.target.value })}
                      data-testid="customer-jobcenter-fax" placeholder="030 / xxxx xxxx" />
                  </Field>
                  <Field label="E-Mail">
                    <input type="email" className="field" value={form.jobcenter_email}
                      onChange={(e) => setForm({ ...form, jobcenter_email: e.target.value })}
                      data-testid="customer-jobcenter-email" />
                  </Field>
                </div>
              </div>
            </div>

            {/* Abrechnungsweg */}
            <div className="border-t border-[#E5E7EB] pt-4">
              <div className="label-eyebrow mb-2">Bevorzugter Abrechnungsweg</div>
              <p className="text-xs text-[#5B6371] mb-2">Wie das Jobcenter die Abrechnung erhalten möchte:</p>
              <div className="grid grid-cols-2 gap-2">
                {BILLING_OPTIONS.filter(o => o.value).map((opt) => (
                  <label key={opt.value} className={`flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer text-sm transition-colors ${
                    form.billing_method === opt.value
                      ? "bg-[#E6F7F7] border-[#0AA09E] text-[#0AA09E] font-medium"
                      : "bg-white border-[#E5E7EB] text-[#5B6371] hover:border-[#0AA09E]"
                  }`}>
                    <input type="radio" name="billing_method" value={opt.value}
                      checked={form.billing_method === opt.value}
                      onChange={(e) => setForm({ ...form, billing_method: e.target.value })}
                      className="accent-[#0AA09E]"
                      data-testid={`customer-billing-${opt.value}`} />
                    {opt.label}
                  </label>
                ))}
              </div>
              {form.billing_method === "portal" && (
                <div className="mt-3">
                  <Field label="Portal-URL oder Bezeichnung">
                    <input className="field" value={form.billing_portal_url}
                      onChange={(e) => setForm({ ...form, billing_portal_url: e.target.value })}
                      data-testid="customer-portal-url"
                      placeholder="z.B. eService Bundesagentur" />
                  </Field>
                </div>
              )}
            </div>

            <Field label="Notizen">
              <textarea className="field" rows={2} value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                data-testid="customer-notes" />
            </Field>

            <div className="flex justify-end gap-3 pt-4 border-t border-[#E5E7EB]">
              <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 text-sm text-[#5B6371]" data-testid="customer-cancel">Abbrechen</button>
              <button type="submit" className="px-5 py-2 bg-[#0AA09E] hover:bg-[#088987] text-white text-sm font-medium rounded-md" data-testid="customer-submit">
                {editing ? "Aktualisieren" : "Speichern"}
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
